import React, { useCallback, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApproveAccrualReviewBody } from "@common/schemas/domains/accrualReview/accrual-review.schema";
import {
    fromMonetaryToInt,
    monetaryValuesEqual,
    roundToCents,
} from "@common/helpers/monetary";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useScope } from "@state/scopeContext.lib";
import { undoAccrualReview } from "@api/accrual-review.api";
import {
    applyUndoToAccrualReviewCache,
    invalidateAccrualMatchCaches,
} from "@components/accounting/accrualReview/accrualReviewCache";
import { useUnmatchTransfer } from "@mutations/transaction";
import { detachFinancialTransactionMatch } from "@lib/transactions";
import { invalidateTransactionDetailsQueries } from "@components/transactions/TransactionDetails/useTransactionDetailsQuery";
import { invalidateTransactionsQueries } from "@components/transactions/useTransactionsQuery";
import {
    removedSuggestionMatchIds,
    suggestedDocumentItemIds,
} from "./linkedDocumentSuggestions";
import {
    AccrualMatchSearchFooterState,
    MatchableItem,
    MatchItemType,
    MatchableTransaction,
} from "./types";
import {
    matchableItemToLockCheckItem,
    useIsAnyMatchableItemLocked,
} from "./matchLock";
import { parseTransactionMatchId } from "./mappers";
import { useAccrualMatch } from "./useAccrualMatch";
import { buildDefaultLineItemAllocations } from "./editAllocation/allocationHelpers";
import { CROSS_ENTITY_BILL_REQUIRES_ACCRUAL_LEDGER_REASON } from "./editAllocation/crossEntityBillGuard";
import {
    hasSelectionLeftAfterUnmatch,
    unmatchedSelectionItemIds,
} from "./schedule/matchResolutions";
import {
    approveAccrualReviewRequest,
    useApproveWithScheduleResolutions,
} from "@components/accounting/schedules/linkedConflict/useApproveWithScheduleResolutions";
import {
    getConfirmLabel,
    getRemovalConfirmLabel,
    getIsUnapprovedDocumentSelection,
    getConfirmedPairItemId,
    getDifferenceActionLabel,
    getIsConfirmDisabled,
    getIsPersistedDocumentRemoval,
    getIsPersistedMatchRemoval,
    getIsPersistedTransactionRemoval,
    getIsRemovalOnlyEdit,
    getIsSelectionAlreadyPersisted,
    getIsSelectionUnchanged,
    getIsSuggestedMatchRemoval,
} from "./transactionMatchFooter.lib";
import { MatchesPanel } from "./MatchesPanel";

const SUGGESTION_REMOVAL_FAILED_MESSAGE =
    "Match saved, but some suggestions couldn't be removed. Reopen the match to try again.";

/**
 * Why the confirm button is disabled, for the clamps the difference row cannot
 * express (it reads $0.00 in all of these). `null` means nothing blocks the
 * selection beyond the amount itself.
 */
function getBlockedReason({
    hasMixedDocTypes,
    hasUnsupportedCrossEntityDoc,
    hasCashBasisOverSelection,
}: {
    hasMixedDocTypes: boolean;
    hasUnsupportedCrossEntityDoc: boolean;
    hasCashBasisOverSelection: boolean;
}): string | null {
    if (hasMixedDocTypes) {
        return "Invoices and bills cannot be matched to the same transaction";
    }
    if (hasUnsupportedCrossEntityDoc) {
        return CROSS_ENTITY_BILL_REQUIRES_ACCRUAL_LEDGER_REASON;
    }
    if (hasCashBasisOverSelection) {
        return "Selected documents exceed the transaction amount — remove one or match them one at a time";
    }
    return null;
}

interface Props {
    transaction: MatchableTransaction;
    sourceItem: MatchableItem;
    searchFooterState: AccrualMatchSearchFooterState | null;
    hasAccrualLedger: boolean;
    /**
     * Called as the user drags the resize handle. Receives the number of px
     * the panel should grow beyond its default height. `0` = default. Always
     * non-negative — the panel's minimum height is its default height.
     * Updates are emitted on every pointermove; downstream applies them as a
     * CSS variable (no React re-renders during drag).
     */
    onMatchesPanelExtraHeightChange: (px: number) => void;
}

export const TransactionMatchFooter: React.FC<Props> = ({
    transaction,
    sourceItem,
    searchFooterState,
    onMatchesPanelExtraHeightChange,
    hasAccrualLedger,
}) => {
    const {
        selectedItems,
        removeItem,
        onOpenEditAllocation,
        onConfirmSuccess,
        onClose,
        isCashBasis,
        persistedItemIds,
        currentTxLineItemMatchAmounts,
    } = useAccrualMatch();
    const { approveWithScheduleResolutions, scheduleResolutionModals } =
        useApproveWithScheduleResolutions();
    const { workspaceId } = useScope();
    const { toast } = useToaster();
    const queryClient = useQueryClient();
    const [isLoading, setIsLoading] = useState(false);
    const [droppedMatchIds, setDroppedMatchIds] = useState(
        () => new Set<number>(),
    );
    const [dismissedSuggestionIds, setDismissedSuggestionIds] = useState(
        () => new Set<string>(),
    );
    const isSubmittingRef = useRef(false);

    // Id of the already-confirmed cash transfer partner (if any). Reversing
    // this pair must hit the unmatch-transfer endpoint, not just drop it from
    // local selection.
    const confirmedPairItemId = getConfirmedPairItemId({
        isCashBasis,
        pairedTransferId: transaction.pairedTransferId,
    });
    const selectedItemIds = useMemo(
        () => selectedItems.map((item) => item.id),
        [selectedItems],
    );
    const initialSelectedIdsRef = useRef<ReadonlySet<string> | null>(null);
    if (initialSelectedIdsRef.current === null) {
        initialSelectedIdsRef.current = new Set(
            selectedItems.map((item) => item.id),
        );
    }
    const initialSelectedIds = initialSelectedIdsRef.current;
    const isRemovalOnlyEdit = getIsRemovalOnlyEdit({
        selectedItemIds,
        initialSelectedIds,
    });
    const isSelectionAlreadyPersisted = getIsSelectionAlreadyPersisted({
        confirmedPairItemId,
        selectedItemIds,
    });
    // The drawer opened with `persistedItemIds` already confirmed on the
    // server — re-submitting that exact selection (added nothing, removed
    // nothing) is a no-op that must not be offered as "Match"/"Confirm
    // match"/"Save changes". Generalizes `isSelectionAlreadyPersisted`
    // (originally scoped to the single cash-transfer-pair case) to any
    // number of already-persisted documents/pairs.
    const suggestionInput = {
        matches: transaction.transactionMatches,
        persistedItemIds,
        excludedMatchIds: droppedMatchIds,
    };
    const suggestedItemIds = suggestedDocumentItemIds(suggestionInput);
    const pendingSuggestionRemovalIds = removedSuggestionMatchIds({
        ...suggestionInput,
        selectedItemIds,
    });
    const isSuggestedMatchRemoval = getIsSuggestedMatchRemoval({
        selectedItemIds,
        persistedItemIds,
        suggestedItemIds,
    });
    const isSelectionUnchanged =
        pendingSuggestionRemovalIds.length === 0 &&
        getIsSelectionUnchanged({
            selectedItemIds,
            persistedItemIds,
        });
    const removalInput = { selectedItemIds, persistedItemIds };
    const isPersistedDocumentRemoval = isCashBasis
        ? getIsPersistedDocumentRemoval(removalInput)
        : getIsPersistedMatchRemoval(removalInput);

    const isConfirmedPairRemoval =
        confirmedPairItemId != null &&
        !selectedItemIds.includes(confirmedPairItemId) &&
        selectedItemIds.every((id) => persistedItemIds.has(id));
    const isPersistedRemoval =
        isPersistedDocumentRemoval || isConfirmedPairRemoval;
    const isPersistedTransactionRemoval =
        getIsPersistedTransactionRemoval(removalInput);
    const isDocumentApproval = getIsUnapprovedDocumentSelection({
        selectedDocumentIds: selectedItems
            .filter(
                (item) =>
                    item.type === MatchItemType.BILL ||
                    item.type === MatchItemType.INVOICE,
            )
            .map((item) => item.id),
        suggestedItemIds,
        dismissedSuggestionIds,
    });

    const unmatchPair = useUnmatchTransfer({
        financialTransaction: { id: transaction.id },
        onSuccess: () => {},
    });

    const selectedTotal = useMemo(
        () =>
            selectedItems.reduce((sum, item) => {
                if (item.type === MatchItemType.TRANSACTION) {
                    return sum + Math.abs(item.amount);
                }
                return sum + Math.abs(item.openAmount ?? item.amount);
            }, 0),
        [selectedItems],
    );

    const transactionAbsAmount = Math.abs(transaction.amount);
    // Transaction-to-transaction matches must land on an exact amount
    // (enforced by the backend for that path), but invoice/bill matches
    // don't: when the selected docs' open amount fully covers (or exceeds)
    // the transaction, the whole transaction applies cleanly to that
    // account with nothing left over, so "Resolve difference" is only
    // actually required when the transaction is bigger than what the
    // selected invoice(s)/bill(s) can absorb.
    const hasTransactionMatch = selectedItems.some(
        (item) => item.type === MatchItemType.TRANSACTION,
    );
    // Invoices (AR) and bills (AP) can never be settled by the same
    // transaction — the backend approval validation rejects a body carrying
    // both `invoiceIds` and `billIds`. Keep such a selection unconfirmable so
    // the user isn't offered a "Confirm match" that only fails on submit.
    const hasMixedDocTypes =
        selectedItems.some((item) => item.type === MatchItemType.INVOICE) &&
        selectedItems.some((item) => item.type === MatchItemType.BILL);
    // Cash-basis matching books a sibling entity's invoice to the Due to/from
    // account, but a sibling entity's bill still needs an accrual ledger, so
    // the backend rejects it. Same reasoning as the mixed-doc-types clamp:
    // don't offer a confirm that only fails on submit.
    const hasUnsupportedCrossEntityDoc =
        isCashBasis &&
        selectedItems.some(
            (item) =>
                item.type === MatchItemType.BILL &&
                item.entityId !== transaction.entity.id,
        );
    // Cash-basis matching allocates greedily into a single group, so several
    // documents whose combined open amount exceeds the transaction would leave
    // one of them unmatched. The backend rejects that shape; keep it
    // unconfirmable here too. One over-covering document is a legitimate
    // partial payment and stays allowed.
    const selectedDocumentCount = selectedItems.filter(
        (item) => item.type !== MatchItemType.TRANSACTION,
    ).length;
    const hasCashBasisOverSelection =
        isCashBasis &&
        selectedDocumentCount > 1 &&
        fromMonetaryToInt(selectedTotal) >
            fromMonetaryToInt(transactionAbsAmount);
    // PRO-12200: the source transaction or any currently selected match
    // sitting in a locked accounting period blocks confirming/saving the
    // match entirely (it would write/reverse a journal entry dated inside
    // the lock).
    const lockCheckItems = useMemo(
        () => [
            matchableItemToLockCheckItem(sourceItem),
            ...selectedItems.map(matchableItemToLockCheckItem),
        ],
        [sourceItem, selectedItems],
    );
    const { isConfirmBlocked: isLockBlocked, lockedReason } =
        useIsAnyMatchableItemLocked(lockCheckItems);
    const blockedReason =
        getBlockedReason({
            hasMixedDocTypes,
            hasUnsupportedCrossEntityDoc,
            hasCashBasisOverSelection,
        }) ?? lockedReason;
    const isDocumentOverCoverage =
        !hasTransactionMatch &&
        fromMonetaryToInt(selectedTotal) >
            fromMonetaryToInt(transactionAbsAmount);
    const isAmountMatched =
        selectedItems.length > 0 &&
        !blockedReason &&
        (monetaryValuesEqual(transactionAbsAmount, selectedTotal) ||
            isDocumentOverCoverage);
    const difference = roundToCents(transactionAbsAmount - selectedTotal);
    // Cash-only entities have no "Resolve difference" flow (it books the
    // remainder to an accrual account), but they can still under-cover a
    // transaction with the selected documents. Surface the figure so a
    // disabled "Match" button is explained rather than silent.
    const hasDocumentSelection = selectedItems.some(
        (item) => item.type !== MatchItemType.TRANSACTION,
    );

    const handleUnmatch = useCallback(
        (item: MatchableItem) => {
            if (suggestedItemIds.includes(item.id)) {
                setDismissedSuggestionIds((current) => {
                    const next = new Set(current);
                    next.add(item.id);
                    return next;
                });
            }
            removeItem(item.id);
        },
        [removeItem, suggestedItemIds],
    );

    const handleConfirm = useCallback(async () => {
        if (
            !workspaceId ||
            isSubmittingRef.current ||
            isSelectionAlreadyPersisted ||
            isSelectionUnchanged
        ) {
            return;
        }

        const persistRemovedSuggestions = async () => {
            if (pendingSuggestionRemovalIds.length === 0) {
                return;
            }
            const results = await Promise.allSettled(
                pendingSuggestionRemovalIds.map((matchId) =>
                    detachFinancialTransactionMatch(transaction.id, matchId),
                ),
            );
            const detachedMatchIds = pendingSuggestionRemovalIds.filter(
                (_, index) => results[index]?.status === "fulfilled",
            );
            if (detachedMatchIds.length > 0) {
                setDroppedMatchIds((current) => {
                    const next = new Set(current);
                    for (const matchId of detachedMatchIds) {
                        next.add(matchId);
                    }
                    return next;
                });
                await invalidateTransactionDetailsQueries(transaction.id);
                await invalidateTransactionsQueries();
            }
            const failure = results.find(
                (result) => result.status === "rejected",
            );
            if (failure?.status === "rejected") {
                throw failure.reason;
            }
        };

        const persistRemovedSuggestionsAfterSave = async (
            savedMessage: string,
        ) => {
            try {
                await persistRemovedSuggestions();
                toast(savedMessage);
            } catch {
                toast(SUGGESTION_REMOVAL_FAILED_MESSAGE, undefined, {
                    variant: "warning",
                });
            }
        };

        if (
            isSuggestedMatchRemoval &&
            !isConfirmedPairRemoval &&
            !isPersistedDocumentRemoval
        ) {
            isSubmittingRef.current = true;
            setIsLoading(true);
            try {
                await persistRemovedSuggestions();
                toast("Match saved");
                onClose?.();
            } catch {
                // error toast handled by axios interceptor
            } finally {
                isSubmittingRef.current = false;
                setIsLoading(false);
            }
            return;
        }

        if (isConfirmedPairRemoval && selectedItems.length === 0) {
            isSubmittingRef.current = true;
            setIsLoading(true);
            try {
                await unmatchPair.mutateAsync();
                await persistRemovedSuggestionsAfterSave("Match saved");
                onClose?.();
            } catch {
                // error toast handled by axios interceptor
            } finally {
                isSubmittingRef.current = false;
                setIsLoading(false);
            }
            return;
        }

        const undoPersistedMatch = async () => {
            const removedPersistedTransfer = [...persistedItemIds].some(
                (id) => id.startsWith("tx-") && !selectedItemIds.includes(id),
            );
            if (removedPersistedTransfer) {
                await unmatchPair.mutateAsync();
            }
            const response = await undoAccrualReview({
                workspaceId,
                transactionId: transaction.id,
            });
            applyUndoToAccrualReviewCache(response);
            invalidateAccrualMatchCaches(queryClient, {
                transactionId: transaction.id,
                skipAccrualReviewList: true,
            });
            await persistRemovedSuggestionsAfterSave("Match saved");
            onClose?.();
        };

        if (isPersistedDocumentRemoval && selectedItems.length === 0) {
            isSubmittingRef.current = true;
            setIsLoading(true);
            try {
                await undoPersistedMatch();
            } catch {
                // error toast handled by axios interceptor
            } finally {
                isSubmittingRef.current = false;
                setIsLoading(false);
            }
            return;
        }

        const transactionMatch = selectedItems.find(
            (item) => item.type === MatchItemType.TRANSACTION,
        );

        const invoiceIds = selectedItems
            .filter((item) => item.type === MatchItemType.INVOICE)
            .map((item) => item.id.replace(/^inv-/, ""));

        const billIds = selectedItems
            .filter((item) => item.type === MatchItemType.BILL)
            .map((item) => item.id.replace(/^bill-/, ""));

        // Whole-document ids take every open line in full, which the accrual
        // split rejects once lines land in several groups and exceed the
        // transaction. Send the same pro-rata split "Edit allocation" shows.
        const lineItemAllocations =
            !isCashBasis && isDocumentOverCoverage
                ? buildDefaultLineItemAllocations({
                      items: selectedItems,
                      transactionAmount: transaction.amount,
                      currentTxMatchAmounts: currentTxLineItemMatchAmounts,
                  })
                : null;

        const body: ApproveAccrualReviewBody = {
            transactionId: transaction.id,
            ...(transactionMatch && {
                matchedTransactionId: parseTransactionMatchId(
                    transactionMatch.id,
                ),
            }),
            ...(lineItemAllocations
                ? { lineItemAllocations }
                : {
                      ...(invoiceIds.length > 0 ? { invoiceIds } : {}),
                      ...(billIds.length > 0 ? { billIds } : {}),
                  }),
        };

        isSubmittingRef.current = true;
        setIsLoading(true);
        try {
            const outcome = await approveWithScheduleResolutions({
                request: approveAccrualReviewRequest({
                    workspaceId,
                    body,
                }),
                hasWorkLeft: (resolutions) =>
                    hasSelectionLeftAfterUnmatch({
                        selectedItems,
                        resolutions,
                    }),
                onUnmatched: (resolutions) => {
                    const dropIds = new Set(
                        unmatchedSelectionItemIds(resolutions),
                    );
                    for (const item of selectedItems) {
                        if (dropIds.has(item.id)) {
                            handleUnmatch(item);
                        }
                    }
                },
                onApproved: async (response) => {
                    await persistRemovedSuggestionsAfterSave(
                        isDocumentApproval ? "Approved" : "Match saved",
                    );
                    onConfirmSuccess?.(response);
                },
            });
            if (outcome === "skipped" && isPersistedDocumentRemoval) {
                await undoPersistedMatch();
            }
        } catch {
            // toasted by approveWithScheduleResolutions
        } finally {
            isSubmittingRef.current = false;
            setIsLoading(false);
        }
    }, [
        workspaceId,
        selectedItems,
        transaction.id,
        transaction.amount,
        isCashBasis,
        isDocumentOverCoverage,
        currentTxLineItemMatchAmounts,
        onConfirmSuccess,
        toast,
        isSelectionAlreadyPersisted,
        isDocumentApproval,
        isSelectionUnchanged,
        isSuggestedMatchRemoval,
        pendingSuggestionRemovalIds,
        isPersistedDocumentRemoval,
        isConfirmedPairRemoval,
        persistedItemIds,
        selectedItemIds,
        unmatchPair,
        queryClient,
        onClose,
        approveWithScheduleResolutions,
        handleUnmatch,
    ]);

    const handleOpenDrawer = useCallback(() => {
        onOpenEditAllocation?.(sourceItem);
    }, [onOpenEditAllocation, sourceItem]);

    const isAlreadyApproved = transaction.accrualAccountId != null;
    const isSavingRemoval =
        isPersistedTransactionRemoval ||
        isPersistedRemoval ||
        isSuggestedMatchRemoval ||
        isRemovalOnlyEdit;
    const confirmLabel = isSavingRemoval
        ? getRemovalConfirmLabel()
        : getConfirmLabel({
              isCashBasis,
              isAlreadyApproved,
              isSelectionAlreadyPersisted,
              isDocumentApproval,
          });
    const isConfirmDisabled = getIsConfirmDisabled({
        isAmountMatched,
        isPersistedDocumentRemoval:
            isPersistedRemoval || isSuggestedMatchRemoval,
        isLoading,
        isSelectionAlreadyPersisted,
        isSelectionUnchanged,
        hasBlockedReason: blockedReason != null || isLockBlocked,
    });
    // Surfaced both inline (blockedReasonRow below) and as a tooltip on the
    // disabled Confirm button itself (PRO-12200) so the reason is visible
    // without scanning the rest of the panel.
    const confirmDisabledTooltip =
        isConfirmDisabled && blockedReason ? blockedReason : undefined;

    return (
        <MatchesPanel
            title={
                hasAccrualLedger
                    ? "Matches to be confirmed"
                    : "Review your match"
            }
            sourceLabel={
                hasAccrualLedger ? "Transaction to match" : "This transaction"
            }
            sourceItem={sourceItem}
            matchesLabel={hasAccrualLedger ? "Matches" : "Matched to"}
            selectedItems={selectedItems}
            onUnmatch={handleUnmatch}
            difference={
                hasAccrualLedger || hasDocumentSelection ? difference : null
            }
            blockedReason={blockedReason}
            searchFooterState={searchFooterState}
            onMatchesPanelExtraHeightChange={onMatchesPanelExtraHeightChange}
            actions={
                <>
                    {(hasAccrualLedger || hasDocumentSelection) && (
                        <Button
                            variant="primary"
                            size="md"
                            onClick={handleOpenDrawer}
                        >
                            {getDifferenceActionLabel(difference)}
                        </Button>
                    )}
                    <WithHotkey
                        hotKey="mod+enter"
                        hotKeyDisplayPosition="inline"
                        tooltip={confirmDisabledTooltip}
                        hotKeyOptions={{
                            description: confirmLabel,
                            enabled: !isConfirmDisabled,
                            preventDefault: true,
                            enableOnFormTags: true,
                        }}
                    >
                        {(hotkeyHint) => (
                            <Button
                                variant="primary"
                                size="md"
                                disabled={isConfirmDisabled}
                                onClick={handleConfirm}
                                rightIcon={hotkeyHint}
                            >
                                {confirmLabel}
                            </Button>
                        )}
                    </WithHotkey>
                </>
            }
        >
            {scheduleResolutionModals}
        </MatchesPanel>
    );
};
